import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableProps,
} from '@mui/material';
import useComponentSize from '@rehooks/component-size';
import { useRef } from 'react';
import * as React from 'react';

import { AsyncContent } from '~/components/AsyncContent';

import { tableRowHeight, useSxStyles } from './useStyles';

export interface TableRowComponentProps<TR> {
  tableRow: TR;
  isTabDisabled?: boolean;
}

export interface TableHeaderComponentProps {}

export interface Props<TR> {
  tableRowsLoading: boolean;
  tableRows?: TR[];
  failedToLoadTableItems: boolean;
  noDataMessage: string;
  getIdentifier: (item: TR) => string | number;
  onResize: (noOfFittingTableItems: number) => void;
  tableProps?: TableProps;
  isTabDisabled?: boolean;
}

export const ContentComponentFactory = <TR extends {}>(
  TableRowComponent: React.ComponentType<TableRowComponentProps<TR>>,
  TableHeaderComponent?: React.ComponentType<TableHeaderComponentProps>,
) => {
  const Component: React.FC<Props<TR>> = ({
    tableRowsLoading,
    tableRows,
    getIdentifier,
    failedToLoadTableItems,
    noDataMessage,
    onResize,
    tableProps,
    isTabDisabled,
  }) => {
    const sxStyles = useSxStyles();
    const containerRef = useRef<HTMLDivElement>(null);
    const { height: containerHeight } = useComponentSize(containerRef);
    const tHeadId = 'id';
    const noOfFittingTableItems =
      (containerHeight -
        (document.getElementById(tHeadId)?.clientHeight || 0)) /
      tableRowHeight;

    React.useEffect(() => {
      onResize(noOfFittingTableItems);
    }, [noOfFittingTableItems, onResize]);

    return (
      <Box ref={containerRef} sx={sxStyles.refDiv}>
        <AsyncContent
          isLoading={tableRowsLoading}
          failedToLoad={failedToLoadTableItems}
          noData={!tableRows || !tableRows.length}
          noDataMessage={noDataMessage}
          customSxStyles={{ errorMessage: sxStyles.contentPadding }}
        >
          <TableContainer sx={sxStyles.tableContainer}>
            <Table sx={sxStyles.table} {...tableProps}>
              {TableHeaderComponent && (
                <TableHead id={tHeadId}>
                  <TableHeaderComponent />
                </TableHead>
              )}
              <TableBody>
                {tableRows?.map((tableRow) => (
                  <TableRowComponent
                    key={getIdentifier(tableRow)}
                    tableRow={tableRow}
                    isTabDisabled={isTabDisabled}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AsyncContent>
      </Box>
    );
  };

  return Component;
};
